import { Box, keyframes, useTheme, useMediaQuery } from '@mui/material';
import {
  getAppTheme,
  useAppThemeColors,
  useAppThemed,
} from '../../helpers/themeHelper';
import { Antlers, Ice, Nose } from './LabsCardHeaderHolidays';
import { globals } from '../../theme';

const animBadge = (y: number) =>
  `${keyframes`
  0% {
    transform: translateY(-${y}px) rotate(5deg);
  }
  50% {
    transform: translateY(-${y}px) rotate(-5deg);
  }
  100% {
    transform: translateY(-${y}px) rotate(5deg);
  }
`} 4s ease-in-out infinite`;

const animBadgeLightning = (delay: number, colors: string[]) =>
  `${keyframes`
  0% {
    fill: ${colors[0]};
    transform: scale(1);
  }
  10% {
    fill: ${colors[1]};
  }
  20% {
    fill: ${colors[0]};
  }
  30% {
    fill: ${colors[1]};
  }
  40 % {
    fill: ${colors[0]};
  }
  50% {
    fill: ${colors[1]};
    transform: scale(1.1);
  }
  60% {
    fill: ${colors[0]};
  }
  70% {
    fill: ${colors[1]};
  }
  80% {
    fill: ${colors[0]};
  }
  90% {
    fill: ${colors[1]};
  }
  100% {
    fill: ${colors[0]};
    transform: scale(1);
  }
`} 1s ${delay}ms ease-in-out infinite`;

const animBadgeRing = (colors: string[]) =>
  `${keyframes`
  0% {
    fill: ${colors[0]};
  }
  25% {
    fill: ${colors[1]};
  }
  50% {
    fill: ${colors[2]};
  }
  75% {
    fill: ${colors[3]};
  }
  100% {
    fill: ${colors[0]};
  }
`} 2s linear infinite`;

const animBadgeOverlay = keyframes`
  0% {
    transform: rotate(-45deg) translateY(-100%);
  }
  100% {
    transform: rotate(-45deg) translateY(100%);
  }
`;

const Badge: React.FC<{
  size: number;
  appTheme: string;
}> = ({ size, appTheme }) => {
  const overlayColor = '253, 129, 245';
  const colors = useAppThemed({
    default: {
      title: globals.colors.white,
      background: globals.colors.primary,
    },
    holidays: {
      title: globals.colors.black,
      background: '#FEF5D2',
    },
  });
  const themeColors = useAppThemeColors();
  const isHolidays = appTheme === 'holidays';
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        width: size,
        height: size,
        borderRadius: 999,
        overflow: 'hidden',
        boxShadow: `0px 4px 16px 0px ${globals.colors.black80}`,
        animation: animBadge(size / 2),
        svg: {
          '&.badge': {
            width: '100%',
            height: 'auto',
            overflow: 'visible',
            '#lightning': {
              '& path:nth-of-type(1)': {
                transformOrigin: 'center',
                animation: animBadgeLightning(0, [
                  themeColors.quaternary,
                  themeColors.primary,
                ]),
              },
              '& path:nth-of-type(2)': {
                transformOrigin: 'center',
                animation: animBadgeLightning(200, [
                  themeColors.quaternary,
                  themeColors.primary,
                ]),
              },
              '& path:nth-of-type(3)': {
                transformOrigin: 'center',
                animation: animBadgeLightning(400, [
                  themeColors.quaternary,
                  themeColors.primary,
                ]),
              },
              '& path:nth-of-type(4)': {
                transformOrigin: 'center',
                animation: animBadgeLightning(600, [
                  themeColors.quaternary,
                  themeColors.primary,
                ]),
              },
            },
            '#ring': {
              animation: animBadgeRing([
                themeColors.primary,
                themeColors.secondary,
                themeColors.tertiary,
                themeColors.quaternary,
              ]),
            },
          },
        },
      }}>
      {/* SVG */}
      <svg
        className="badge"
        width="128"
        height="128"
        viewBox="0 0 128 128"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        {/* Backgrounds */}
        <path
          id="bk-outer"
          d="M83.917 124.789C67.6787 130.109 50.3413 128.788 35.0993 121.067C19.8573 113.346 8.53131 100.152 3.21104 83.9169C-2.10923 67.6786 -0.785405 50.344 6.93273 35.0993C14.6536 19.8573 27.8475 8.53123 44.083 3.21096C60.3213 -2.1093 77.6587 -0.788257 92.9007 6.93265C108.143 14.6536 119.469 27.8474 124.789 44.0829C130.109 60.3213 128.788 77.6586 121.067 92.9006C113.346 108.143 100.153 119.469 83.917 124.789Z"
          fill={globals.colors.black}
        />
        <path
          id="bk-inner"
          d="M82.3767 120.085C67.3956 124.992 51.4015 123.773 37.339 116.652C23.2765 109.53 12.8275 97.3578 7.92073 82.3767C3.01398 67.3956 4.23234 51.4015 11.3538 37.339C18.4752 23.2765 30.6477 12.8275 45.6288 7.92073C60.61 3.01398 76.6041 4.23234 90.6666 11.3538C104.729 18.4752 115.178 30.6477 120.085 45.6288C124.992 60.61 123.773 76.6041 116.652 90.6666C109.53 104.729 97.3578 115.178 82.3767 120.085Z"
          fill={colors.background}
        />
        {/* Lightning */}
        <g id="lightning" fill={themeColors.quaternary}>
          <path d="M102.323 56.9744L107.882 51.088L104.049 49.8502L115.089 43.0535L109.747 49.0398L113.579 50.2776L102.323 56.9744Z" />
          <path d="M91.9793 88.4463L97.6576 94.2189L93.8526 95.5344L105.032 102.101L99.567 96.2255L103.372 94.91L91.9793 88.4463Z" />
          <path d="M35.8403 88.9043L30.4007 94.9017L34.2584 96.0618L23.3542 103.078L28.5773 96.986L24.7197 95.8259L35.8403 88.9043Z" />
          <path d="M26.0463 57.0769L20.4845 51.1933L24.3145 49.9527L13.2687 43.1643L18.6168 49.1479L14.7841 50.3884L26.0463 57.0769Z" />
        </g>
        {/* Logo */}
        <path
          id="logo"
          d="M100.316 58.9476C100.344 56.7856 99.7613 54.6708 98.6289 52.828C97.4966 50.9935 95.8703 49.5171 93.9442 48.5762L93.811 48.5041L93.9053 40.9858L89.7896 40.933C89.1179 40.9247 88.4463 40.958 87.7803 41.033H87.647L87.5638 40.9247C84.8245 37.228 81.2777 34.2224 77.1952 32.1298C73.1127 30.0427 68.6029 28.9215 64.0208 28.8632C59.436 28.805 54.9012 29.8124 50.7687 31.8051C46.6335 33.7977 43.0145 36.7118 40.1837 40.3391L40.106 40.4418H39.9728C39.3012 40.3475 38.6295 40.2975 37.9579 40.2892L33.9254 40.2392L33.831 47.7492L33.6978 47.8186C31.7412 48.7122 30.0788 50.1471 28.9076 51.951C27.7309 53.755 27.0954 55.8614 27.0676 58.0262C27.0398 60.1881 27.6227 62.3029 28.755 64.1457C29.8873 65.9802 31.5136 67.4567 33.4397 68.3975L33.5729 68.4558V68.6084C33.4702 76.6013 36.5314 84.2972 42.0876 90.0227C47.6437 95.7426 55.2259 99.0147 63.1855 99.1146C71.1367 99.2145 78.8049 96.1312 84.5026 90.5556C90.2003 84.9772 93.453 77.3534 93.5529 69.3605V69.2079L93.6889 69.1524C95.6455 68.2587 97.3079 66.8239 98.4791 65.02C99.6558 63.216 100.291 61.1095 100.319 58.9448L100.316 58.9476ZM48.0018 42.8702C50.0999 40.7971 52.5921 39.1541 55.3202 38.0467C58.0567 36.9394 60.9763 36.3899 63.9237 36.4259C66.8711 36.462 69.7824 37.0865 72.4827 38.2632C75.1915 39.4372 77.6337 41.1356 79.6791 43.2671L79.8928 43.5002L79.6097 43.6501C78.4608 44.2662 77.359 44.96 76.3071 45.7288L76.1795 45.812L76.0546 45.7399C72.2774 43.7389 68.0978 42.6371 63.8293 42.5289C59.5581 42.5289 55.3535 43.528 51.5292 45.4346L51.396 45.504L51.2849 45.4179C50.2525 44.6214 49.1618 43.8999 48.0295 43.256L47.752 43.07L47.999 42.8702H48.0018ZM78.766 84.4388C74.6197 88.5018 69.0414 90.7443 63.2465 90.6694C57.4572 90.5972 51.9288 88.2188 47.888 84.0502C43.8443 79.8845 41.613 74.2756 41.6851 68.4613L41.9238 49.4033L42.218 49.4838C46.0285 50.4912 49.592 54.7485 50.419 55.7893C52.198 54.1047 54.285 52.7864 56.5775 51.926C58.8699 51.0657 61.2983 50.6633 63.7461 50.7493C66.1883 50.7243 68.614 51.1823 70.8842 52.1064C73.1488 53.0306 75.2109 54.3933 76.9454 56.1223C77.7919 55.1038 81.4359 50.9436 85.3019 50.025L85.5961 49.9528L85.3574 69.0108C85.2852 74.8279 82.9124 80.3757 78.766 84.4416V84.4388Z"
          fill={globals.colors.black}
        />
        {/* Ring */}
        {!isHolidays && (
          <path
            id="ring"
            d="M118.883 71.9361C119.011 67.8342 111.67 64.1014 99.986 61.5315C99.6835 62.7665 99.1757 63.9487 98.4763 65.0228C98.0405 65.6944 97.5354 66.3161 96.9721 66.8767C100.025 68.2893 101.823 69.9823 101.762 71.9333C101.676 74.7169 97.8491 76.9094 92.0209 78.4886C89.1152 79.2768 85.7071 79.9096 82.0187 80.3841C76.1295 81.1446 69.5215 81.5081 63.0661 81.4554C56.8661 81.4054 50.5522 80.9725 44.8906 80.1788C41.2577 79.6709 37.8968 79.0159 35.0077 78.2194C28.8826 76.5292 24.889 74.2063 24.9806 71.3116C25.0444 69.2995 27.062 67.5955 30.3813 66.2078C29.7652 65.589 29.2157 64.8979 28.7522 64.1458C28.1749 63.205 27.7392 62.192 27.4561 61.1401C16.1467 63.5214 8.96425 67.0543 8.83936 71.048C8.68117 76.1546 20.0988 80.6894 37.0698 83.1206C40.5528 83.6201 44.2717 84.0309 48.1738 84.3389C53.0167 84.7219 58.1372 84.9467 63.4408 84.9911C68.6917 85.0328 73.7788 84.894 78.5995 84.5943C82.5294 84.35 86.2843 84.0031 89.809 83.5591C106.983 81.4082 118.725 77.0482 118.886 71.9305L118.883 71.9361Z"
            fill={themeColors.tertiary}
          />
        )}
        {/* Titles */}
        <g id="title-1" fill={colors.title}>
          <path d="M26.9649 29.6931C27.7364 30.2509 28.3137 30.8309 28.7022 31.4332C29.0908 32.0354 29.3156 32.6488 29.3766 33.2704C29.4377 33.8921 29.3572 34.5221 29.1352 35.1549C28.9132 35.7904 28.5774 36.4176 28.1278 37.0393L27.3174 38.1577L32.0548 41.588L29.8401 44.6464L16.7851 35.1965L19.9711 30.7948C20.393 30.2093 20.8537 29.7236 21.3532 29.3378C21.8528 28.9493 22.3912 28.6912 22.9685 28.5607C23.5457 28.4303 24.1674 28.4497 24.8335 28.6162C25.4968 28.7855 26.21 29.1435 26.9677 29.6931H26.9649ZM24.7974 32.843C24.2146 32.4212 23.7289 32.2186 23.3487 32.2352C22.9657 32.2519 22.6188 32.4795 22.3024 32.9152L21.5336 33.9781L24.778 36.326L25.5079 35.3186C25.8326 34.8718 25.9436 34.4555 25.8437 34.0697C25.7438 33.6839 25.3941 33.276 24.7974 32.843Z" />
          <path d="M36.9366 35.4657L27.0065 22.7714L30.0538 20.3874L37.9135 30.4341L41.8655 27.3424L43.9359 29.99L36.9366 35.4657Z" />
          <path d="M51.9871 26.5431L50.3385 23.9759L47.3801 25.1332L47.9351 28.1306L44.2467 29.5737L42.2485 13.0468L45.9369 11.6008L55.6727 25.0944L51.9843 26.5403L51.9871 26.5431ZM48.3181 20.8343C47.7603 19.9156 47.2469 19.0747 46.7723 18.3115C46.3005 17.5483 45.8953 16.8378 45.5539 16.1801C45.751 16.8933 45.9425 17.6898 46.1284 18.5641C46.3144 19.4383 46.5114 20.4124 46.7168 21.4865L46.8167 21.9916L48.5957 21.295L48.3181 20.8343Z" />
          <path d="M63.4241 18.4142L63.7683 24.6448L59.905 24.8585L59.5637 18.6973L54.693 9.0059L58.9476 8.77L59.8023 10.8681C60.1576 11.7257 60.4712 12.55 60.746 13.3409C61.0207 14.1319 61.2205 14.8341 61.3454 15.4418C61.387 14.8257 61.5064 14.1069 61.6979 13.2882C61.8922 12.4695 62.117 11.6175 62.3751 10.7266L62.994 8.54798L67.1763 8.31763L63.4214 18.417L63.4241 18.4142Z" />
          <path d="M68.9109 24.7919L72.627 9.10859L76.3904 9.99946L73.4513 22.4106L78.3359 23.5679L77.5615 26.84L68.9137 24.7919H68.9109Z" />
          <path d="M79.1712 27.7365L86.2288 13.2466L89.7257 14.9506L82.6681 29.4405L79.1685 27.7365H79.1712Z" />
          <path d="M95.3707 33.2899C94.8934 33.8699 94.366 34.3084 93.7888 34.597C93.2115 34.8884 92.6148 35.0383 92.0015 35.0494C91.3854 35.0605 90.7637 34.9467 90.1365 34.708C89.5065 34.4694 88.9015 34.1113 88.3214 33.634C87.8357 33.2343 87.4361 32.8264 87.1225 32.4101C86.8089 31.9938 86.5674 31.5553 86.3981 31.1001C86.2288 30.6422 86.115 30.1676 86.0623 29.6764C86.0096 29.1852 85.9929 28.6606 86.0151 28.1028L89.5481 27.9363C89.5537 28.7744 89.623 29.4627 89.759 30.0011C89.8922 30.5395 90.1143 30.9336 90.4223 31.1889C90.7304 31.4415 91.0523 31.5719 91.3854 31.5775C91.7184 31.583 92.0265 31.4137 92.3095 31.0696C92.4566 30.892 92.5704 30.7171 92.6509 30.5451C92.7314 30.373 92.773 30.1732 92.773 29.9428C92.773 29.7153 92.7231 29.4461 92.6287 29.138C92.5343 28.8299 92.3706 28.4553 92.143 28.0084C91.7489 27.2286 91.4464 26.5292 91.2327 25.9048C91.019 25.2831 90.9052 24.7058 90.8913 24.1785C90.8775 23.6512 90.9663 23.1517 91.1522 22.6798C91.341 22.208 91.6351 21.7307 92.0348 21.245C92.4538 20.7344 92.9312 20.3347 93.4696 20.0405C94.008 19.7491 94.577 19.5743 95.1764 19.5215C95.7759 19.4688 96.3948 19.5438 97.0303 19.7491C97.6659 19.9545 98.282 20.2986 98.8731 20.7871C99.3477 21.1756 99.7363 21.5669 100.042 21.9555C100.347 22.3468 100.58 22.752 100.741 23.1711C100.902 23.5929 101.013 24.0314 101.074 24.4893C101.135 24.9473 101.166 25.4413 101.163 25.9769L97.5798 26.1323C97.5715 25.7077 97.5604 25.3469 97.5465 25.0472C97.5327 24.7475 97.4994 24.4866 97.4439 24.2645C97.3883 24.0397 97.3051 23.8483 97.1913 23.6845C97.0775 23.5208 96.9249 23.3626 96.7362 23.2044C96.4503 22.9713 96.1589 22.8797 95.8592 22.9296C95.5594 22.9796 95.3096 23.1239 95.1154 23.3626C94.9794 23.5291 94.8822 23.6956 94.8212 23.8649C94.7601 24.0342 94.7407 24.2312 94.7546 24.4533C94.7685 24.6753 94.8323 24.9278 94.935 25.2137C95.0377 25.4968 95.1931 25.8437 95.3957 26.2489C95.8009 27.0371 96.1034 27.7503 96.3088 28.3831C96.5114 29.0187 96.614 29.6126 96.614 30.1676C96.614 30.7227 96.5114 31.2528 96.3115 31.7523C96.1089 32.2519 95.7953 32.7625 95.3652 33.2843L95.3707 33.2899Z" />
          <path d="M107.618 36.1068L97.0581 43.2588L94.9017 40.0755L105.462 32.9235L103.627 30.2176L106.411 28.3332L112.233 36.9311L109.45 38.8155L107.615 36.1096L107.618 36.1068Z" />
        </g>
        <g id="title-2" fill={colors.title}>
          <path d="M55.7115 111.573C55.4368 112.516 55.076 113.33 54.6375 114.018C54.1962 114.703 53.6772 115.247 53.0805 115.65C52.4838 116.052 51.8094 116.294 51.0573 116.377C50.3052 116.46 49.4782 116.371 48.5818 116.108C46.8278 115.594 45.6649 114.542 45.0904 112.952C44.5159 111.362 44.6242 109.217 45.4124 106.522C46.1672 103.944 47.2052 102.104 48.5263 101.005C49.8473 99.9027 51.4431 99.628 53.3137 100.175C54.1241 100.411 54.7818 100.716 55.2897 101.085C55.7976 101.457 56.1833 101.895 56.4525 102.4C56.719 102.908 56.891 103.483 56.9687 104.124C57.0437 104.768 57.0576 105.473 57.0104 106.242L53.1777 106.225C53.2609 105.337 53.2609 104.665 53.1777 104.21C53.0944 103.755 52.8252 103.458 52.3701 103.325C52.0453 103.23 51.7484 103.236 51.4792 103.341C51.21 103.447 50.9519 103.669 50.7104 104.005C50.4662 104.343 50.2275 104.809 49.9888 105.403C49.7502 106 49.5032 106.738 49.2423 107.626C48.962 108.587 48.7649 109.391 48.6567 110.041C48.5457 110.69 48.5152 111.22 48.5623 111.637C48.6095 112.05 48.7289 112.367 48.9204 112.583C49.1118 112.8 49.3783 112.958 49.7169 113.058C50.3191 113.235 50.7937 113.188 51.135 112.922C51.4764 112.655 51.7373 112.211 51.9205 111.592L52.1092 110.951L50.3191 110.427L51.1461 107.599L56.4276 109.144L55.7171 111.576L55.7115 111.573Z" />
          <path d="M70.3652 106.869C70.3818 107.821 70.2652 108.631 70.0155 109.303C69.7657 109.974 69.4105 110.521 68.9498 110.946C68.4891 111.368 67.9368 111.681 67.2957 111.884C66.6546 112.086 65.9496 112.195 65.1837 112.208L63.8016 112.231L63.9015 118.078L60.127 118.142L59.8523 102.029L65.2863 101.937C66.0079 101.926 66.674 102.001 67.2818 102.167C67.8896 102.334 68.4224 102.609 68.872 102.992C69.3244 103.375 69.6824 103.883 69.9461 104.515C70.2097 105.148 70.3513 105.933 70.3652 106.869ZM66.5463 107.027C66.5352 106.305 66.4048 105.797 66.1633 105.503C65.9191 105.209 65.5306 105.065 64.9922 105.073L63.6794 105.095L63.7488 109.1L64.9922 109.078C65.5444 109.07 65.9441 108.909 66.1939 108.598C66.4409 108.287 66.5602 107.762 66.5463 107.027Z" />
          <path d="M78.6189 102.742L82.5071 114.889L78.8465 116.061L74.9583 103.913L71.8444 104.909L70.8203 101.707L80.7087 98.54L81.7328 101.743L78.6189 102.739V102.742Z" />
        </g>
      </svg>
      {/* Holidays nose! */}
      {isHolidays && <Nose />}
      {/* Shine overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          background: `linear-gradient(to bottom, rgba(${overlayColor},0) 0%,rgba(${overlayColor},0.6) 50%, rgba(${overlayColor},0) 99%, rgba(${overlayColor},0) 100%)`,
          transform: 'rotate(-45deg) translateY(-100%)',
          animation: `${animBadgeOverlay} 1s 1s forwards`,
        }}
      />
    </Box>
  );
};

interface LabsCardHeaderProps {
  badge: boolean;
  embed: boolean;
  children?: React.ReactNode;
}

const LabsCardHeader: React.FC<LabsCardHeaderProps> = ({
  badge,
  embed,
  children,
}) => {
  const theme = useTheme();
  const appTheme = getAppTheme();
  const isHolidays = appTheme === 'holidays';
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const badgeSize = isLg ? 144 : 104;

  return (
    <Box
      sx={(theme) => ({
        position: 'relative',
        width: '100%',
        display: 'flex',
        padding: badge
          ? `calc(${badgeSize / 2}px + ${theme.spacing(6)}) ${theme.spacing(
              4,
            )} ${theme.spacing(4)}`
          : `${theme.spacing(6)} ${theme.spacing(4)} ${theme.spacing(4)}`,
        flexDirection: 'column',
        alignItems: 'center',
      })}>
      {/* Holidays ice and badge antlers */}
      {isHolidays && !embed && (
        <>
          <Ice sx={{ zIndex: badge ? 0 : 999 }} />
          {badge && <Antlers />}
        </>
      )}

      {/* Badge */}
      {badge && <Badge size={badgeSize} appTheme={appTheme} />}

      {/* Children */}
      {children}
    </Box>
  );
};

export default LabsCardHeader;
